import { RootState } from "@app/store"
import {
  selectCourseClasses,
  selectCourseInstances,
} from "@features/courseInstance/courseInstanceSelectors"
import { selectTerms } from "@features/term/termSelector"
import { selectAllTracks } from "@features/tracks/trackSelectors"
import { ProgramDto, TrackDto } from "@masterschool/course-builder-api"
import { createSelector } from "@reduxjs/toolkit"
import { calculateCourseInstanceIdentifiers } from "@utils/courseInstanceUtils"
import {
  ComparableDate,
  formatDateAsDayLongMonthAndYear,
  formatDateAsYearAndLongMonth,
} from "@utils/date"
import { domainToDisplayName } from "@utils/domainUtils"

export const selectPrograms = (state: RootState) => {
  return state.program.programs
}

export const selectProgramsWithTracks = createSelector(
  [selectPrograms, selectAllTracks],
  (programs, tracks): (ProgramDto & { track: TrackDto | undefined })[] => {
    return programs.map((p) => {
      return {
        ...p,
        track: tracks.find((t) => t.id === p.syllabusId),
      }
    })
  },
)

export const selectProgramsSyllabusPage = (state: RootState) =>
  state.program.syllabusesPage

export const selectProgram = createSelector(
  [
    (state: RootState) => {
      return state.program.programs
    },
    (_, programId: string | undefined) => programId,
  ],
  (programs, programId: string | undefined) => {
    if (!programId) {
      return undefined
    }
    return programs.find((p) => p.id === programId)
  },
)

export const selectClass = (classId?: string) =>
  createSelector(
    (state: RootState) => {
      return state.program.programIdToClasses
    },
    (programIdToClasses) => {
      if (!classId) {
        return undefined
      }
      return Object.values(programIdToClasses)
        .flat()
        .find((c) => c.id === classId)
    },
  )

export const selectClassesForProgram = (programId: string | undefined) =>
  createSelector(
    (state: RootState) => {
      return state.program.programIdToClasses
    },
    (programIdToClasses) => {
      if (!programId || !programIdToClasses[programId]) {
        return []
      }
      return programIdToClasses[programId] ?? []
    },
  )

export const selectFilteredStudents = (programId: string, classId?: string) =>
  createSelector(
    [
      selectClassesForProgram(programId),
      (state) => state.program.studentsPage.search,
    ],
    (classes, search) => {
      return classes
        .filter((c) => !classId || c.id === classId)
        .flatMap((c) => {
          return c.students.map((s) => ({ ...s, classId: c.id }))
        })
        .filter(
          (s) =>
            s.classId.includes(search) ||
            (s.firstName + " " + s.lastName)
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            s.email.toLowerCase().includes(search.toLowerCase()),
        )
    },
  )

export const selectStudents = (programId?: string, classId?: string) =>
  createSelector([selectClassesForProgram(programId)], (classes) =>
    classes
      .filter((c) => !classId || c.id === classId)
      .flatMap((c) =>
        c.students.map((s) => ({ ...s, className: c.name, classId: c.id })),
      ),
  )

export const selectStudent = (
  programId?: string,
  classId?: string,
  studentId?: string,
) =>
  createSelector([selectStudents(programId, classId)], (students) =>
    students.find((s) => s.id === studentId),
  )

export const filterProgramBySearchText = (
  programs: ProgramDto[],
  searchText: string,
) => {
  if (searchText === "") {
    return programs
  }

  const needle = searchText.toLowerCase()

  // Everything a row (or the program's compound display name, e.g.
  // "Data Analysis June 2026") shows must be searchable: school, cohort
  // month, the full start date, and language.
  return programs.filter((program) => {
    const school = domainToDisplayName(program.domain)
    const cohort = formatDateAsYearAndLongMonth(program.startDate)
    const startDate = formatDateAsDayLongMonthAndYear(program.startDate)
    return [
      `${school} ${cohort}`,
      `${school} ${startDate}`,
      program.language,
    ].some((value) => value.toLowerCase().includes(needle))
  })
}

export const selectProgramByStudentId = (studentId: string) =>
  createSelector(
    (state: RootState) => {
      return Object.keys(state.program.programIdToClasses).filter((programId) =>
        state.program.programIdToClasses[programId].some((cl) =>
          cl.students.find((s) => s.id === studentId),
        ),
      )
    },
    (programs) => programs,
  )

export const selectProgramCourseInstanceIdentifiers = createSelector(
  [
    (_, programId: string) => programId,
    selectPrograms,
    selectAllTracks,
    selectTerms,
  ],
  (programId, programs, tracks, terms) => {
    const program = programs.find((p) => p.id === programId)
    const track = tracks.find((t) => t.id === program?.syllabusId)
    if (!track || !program) {
      return []
    }
    const filteredTerms = terms
      .filter((term) =>
        new ComparableDate(new Date(term.startDate)).afterOrEqual(
          new Date(program.startDate),
        ),
      )
      .slice(0, track.terms.length)

    return calculateCourseInstanceIdentifiers(track.terms, filteredTerms)
  },
)

// The course_class IDs for each of the given programs, keyed by program ID.
// This is exactly the chain the program calendar's useGetCourseClassCalendars
// walks (program -> course-instance identifiers -> instances -> classes), but
// for many programs at once and as a single memoized selector — so the main
// calendar can resolve every running program's classes reactively (one
// useAppSelector) instead of a store.getState() snapshot that runs before
// fetchAppData's parallel slices have settled. Recomputes only when programIds
// or the underlying slices change, so the result reference is stable for
// downstream effects once the data is loaded.
export const selectClassIdsByProgram = createSelector(
  [
    (_: RootState, programIds: string[]) => programIds,
    selectPrograms,
    selectAllTracks,
    selectTerms,
    selectCourseInstances,
    selectCourseClasses,
  ],
  (programIds, programs, tracks, terms, courseInstances, courseClasses) => {
    const byProgram: Record<string, string[]> = {}
    for (const programId of programIds) {
      const program = programs.find((p) => p.id === programId)
      const track = tracks.find((t) => t.id === program?.syllabusId)
      if (!program || !track) {
        byProgram[programId] = []
        continue
      }
      const filteredTerms = terms
        .filter((term) =>
          new ComparableDate(new Date(term.startDate)).afterOrEqual(
            new Date(program.startDate),
          ),
        )
        .slice(0, track.terms.length)
      const identifiers = calculateCourseInstanceIdentifiers(
        track.terms,
        filteredTerms,
      )
      const instanceIds = identifiers.msCompactMap((cii) => {
        const { courseId, termId, majorVersion } = cii
        const instancesForTerm = courseInstances.filter(
          (ci) => ci.courseId === courseId && ci.termId === termId,
        )
        const instanceForMajorVersion = instancesForTerm.find((ci) =>
          majorVersion ? ci.majorVersion === majorVersion : true,
        )
        return (instanceForMajorVersion ?? instancesForTerm[0])?.id
      })
      byProgram[programId] = courseClasses
        .filter((cc) => instanceIds.includes(cc.courseInstanceId))
        .map((cc) => cc.id)
    }
    return byProgram
  },
)
